// The DE profile's Agents-mode tabs (M3-08): Runs (its status line, its verified rate per month
// from `digitalEmployees.verifiedRate` (DC-07, AGT-UI-2), then its runs) and, for agency
// operators, Agents (its composition: lead, specialists and workers, each pinned to a template
// version; `digitalEmployees.agents`, never shown to the client company). The Budget tab is
// `DeBudgetTab` (budgets-screen.tsx).
import {
  Badge,
  EmptyState,
  InfoTip,
  Label,
  ListRow,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { UsersIcon } from "lucide-react";
import { useId } from "react";
import type { Api } from "../../api/client";
import type { AgentProfileRow, DeProfile } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../de/de-status";
import { formatDate, formatDateTime, formatPercent } from "../format";
import { seatQuery, structureKeys } from "../queries";
import { LoadError, Loading, Section } from "../screen-parts";
import { StatusLine } from "./agent-parts";
import { approvalsQuery, deAgentsQuery, runsQuery, verifiedRateQuery } from "./agent-queries";
import { ApprovalStateBadge } from "./approvals-screen";
import { latestRuns } from "./roster-screen";
import { RunsTable } from "./runs-screen";

export function DeRunsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const runs = useInfiniteQuery(runsQuery(api, company.id, { membership_id: de.membership_id }));
  const latest = latestRuns(runs.data?.pages[0]?.data ?? []).get(de.membership_id);
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="de-runs">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Now:</span>
        {runs.isPending ? (
          <span className="text-muted-foreground">…</span>
        ) : (
          <StatusLine run={latest} slug={company.slug} />
        )}
      </div>
      <VerifiedRate api={api} company={company} de={de} />
      <RunsTable
        api={api}
        company={company}
        membershipId={de.membership_id}
        caption={`${workerName(de.display_name)}'s runs, newest first.`}
      />
    </div>
  );
}

/** "2026-10" → "October 2026" (the rate's months are UTC). */
const monthLabel = (key: string) =>
  new Date(`${key}-15T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

/**
 * AGT-UI-2: the DE's verified rate (`de.verified_rate`, DC-07, ADR-0019 §6): of its runs with a
 * verdict in a calendar month (UTC), the share verified. Below `min_runs` the month has no rate:
 * missing is never zero. Shown to the viewers of the DE's runs; for anyone else the section is
 * left out (the API answers 404 / 403).
 */
export function VerifiedRate({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const q = useQuery(verifiedRateQuery(api, company.id, de.membership_id));
  // WF-POLISH (Andrei, 2026-10-27): one line, and none at all until a month has enough runs
  // with a verdict (a block of "Not enough runs yet" said nothing).
  if (!q.data) return q.isError ? <LoadError what="The verified rate" error={q.error} /> : null;
  const months = [...q.data.periods].reverse().filter((p) => p.status === "ok" && p.value !== null);
  if (months.length === 0) return null;
  return (
    <p
      className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm"
      data-slot="de-verified-rate"
    >
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        Verified
        <InfoTip label="About the verified rate">
          Of {workerName(de.display_name)}'s runs with a verdict, the share its checks verified, per
          month (from {q.data.min_runs} runs with a verdict).
        </InfoTip>
      </span>
      {months.map((p) => (
        <span key={p.period_key} className="tabular-nums" data-period={p.period_key}>
          {monthLabel(p.period_key)}{" "}
          <span className="font-semibold">{formatPercent(p.value as number)}</span>{" "}
          <span className="text-muted-foreground">
            ({p.verified} of {p.with_verdict})
          </span>
        </span>
      ))}
    </p>
  );
}

/**
 * The DE's approvals: what it asked for (the approvals list, narrowed to its requests here: the
 * contract has no requester filter) and, for managers, whether its seat holds its chat posts for
 * approval (`seat.settings.require_approval: ["message"]`, M3-03's thread gate).
 */
export function DeApprovalsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const q = useInfiniteQuery(approvalsQuery(api, company.id, {}));
  const mine = (q.data?.pages ?? [])
    .flatMap((p) => p?.data ?? [])
    .filter((a) => a.requested_by_membership_id === de.membership_id);
  const live = !(q.isSuccess && q.data.pages[0] === null);
  return (
    <div className="flex min-w-0 flex-col gap-2" data-slot="de-approvals">
      {isManager(company) && de.seat ? (
        <PostApproval
          api={api}
          company={company}
          seatId={de.seat.id}
          name={workerName(de.display_name)}
        />
      ) : null}
      <Section id="de-approvals-list" title="Asked for">
        {q.isPending ? (
          <Loading label="Loading approvals" rows={2} />
        ) : q.isError ? (
          <LoadError what="Approvals" error={q.error} />
        ) : !live ? (
          <p className="text-sm text-muted-foreground">
            Approvals are not live on this server yet (M3-03).
          </p>
        ) : mine.length ? (
          <div className="flex flex-col rounded-lg border border-border">
            {mine.map((a) => (
              <ListRow
                key={a.id}
                className="border-b border-border last:border-b-0"
                title={a.proposal.title}
                render={
                  <Link
                    to="/c/$slug/agents/approvals/$id"
                    params={{ slug: company.slug, id: a.id }}
                    search={keepFocus}
                  />
                }
                chips={<ApprovalStateBadge state={a.state} />}
                meta={formatDateTime(a.created_at)}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {workerName(de.display_name)} has not asked for an approval yet.
          </p>
        )}
      </Section>
    </div>
  );
}

const asList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/** The thread gate's switch: hold the DE's posts until its approver says yes. */
function PostApproval({
  api,
  company,
  seatId,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  name: string;
}) {
  const qc = useQueryClient();
  const seat = useQuery(seatQuery(api, company.id, seatId));
  const id = useId();
  const settings = (seat.data?.settings ?? {}) as Record<string, unknown>;
  const on = asList(settings.require_approval).includes("message");
  const save = useMutation({
    mutationFn: (next: boolean) => {
      const list = asList(settings.require_approval).filter((k) => k !== "message");
      return api.client.seats.update({
        params: { co: company.id, id: seatId },
        ifMatch: seat.data?.version ?? 0,
        body: {
          settings: { ...settings, require_approval: next ? [...list, "message"] : list },
        },
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: structureKeys.seat(company.id, seatId) }),
  });
  return (
    <Section
      id="de-post-approval"
      title="Approval before posting"
      description={`When on, ${name}'s chat posts wait as an “Awaiting approval” card until the approver decides.`}
    >
      <div className="flex items-center gap-3">
        <Switch
          id={id}
          checked={on}
          disabled={!seat.isSuccess || save.isPending}
          onCheckedChange={(next) => save.mutate(next)}
        />
        <Label htmlFor={id}>Hold {name}'s posts for approval</Label>
      </div>
      {save.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {problemText(save.error, "The setting could not be saved.")}
        </p>
      ) : null}
    </Section>
  );
}

const ROLE_LABELS: Record<AgentProfileRow["role_in_owner"], string> = {
  lead: "Lead",
  specialist: "Specialist",
  worker: "Worker",
};

export function DeAgentsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const q = useQuery(deAgentsQuery(api, company.id, de.membership_id));
  if (q.isPending) return <Loading label="Loading the composition" rows={2} />;
  if (q.isError) return <LoadError what="The composition" error={q.error} />;
  const rows = q.data.data;
  return (
    <Section
      id="de-agents"
      title="Composition"
      description="The agents behind this digital employee, each pinned to a template version. Agency only: the client company never sees this."
    >
      {rows.length ? (
        <Table data-slot="de-agents">
          <TableCaption>Agent profiles of {workerName(de.display_name)}.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Role</TableHead>
              <TableHead scope="col">Template</TableHead>
              <TableHead scope="col">Memory group</TableHead>
              <TableHead scope="col">State</TableHead>
              <TableHead scope="col">Since</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id}>
                <TableHead scope="row" className="font-medium">
                  {ROLE_LABELS[p.role_in_owner]}
                </TableHead>
                <TableCell>
                  <code className="font-mono text-xs">{p.template_id.slice(-8)}</code> · v
                  {p.template_version}
                </TableCell>
                <TableCell className="text-muted-foreground">{p.memory_group_id ?? "—"}</TableCell>
                <TableCell>
                  <Badge variant={p.state === "active" ? "success" : "neutral"}>
                    {p.state === "active" ? "Active" : "Retired"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(p.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          variant="card"
          icon={UsersIcon}
          title="No agent profiles yet"
          description="A deploy through AiGentPanel adds the lead from the DE's template; specialists and workers follow from the template's composition."
        />
      )}
    </Section>
  );
}
